import { BrButton, BrLoading } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Modal } from 'react-dsgov';
import ErrorMessage from '../../../../../../../../../components/errorMessage/errorMessage';
import { DiefCfemContext } from '../../../../../../../../../contexts/DiefCfemContext';
import { ETipoOperacao } from '../../../../../../../../../enums/ETipoOperacao';
import { useNotificacao } from '../../../../../../../../../hooks/useNotificacao';
import { PaginasOutrosRegimes } from '../../../../../../../../../interfaces/IDadosOutrosRegimes';
import { IVendaNacional } from '../../../../../../../../../interfaces/IVendaNacional';
import { useBackEndRequests } from '../../../../../../../lib/backEndRequests';
import ComponenteNCM from '../../../componenteNCM/componenteNCM';
import DadosPessoaRelacionadaForm from './components/dadosPessoaRelacionadaForm/dadosPessoaRelacionadaForm';
import styles from './vendaNacional.module.scss';
import CustomConfirmer from '../../../../../../../../../components/customConfirmer/customConfirmer';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface IVendaNacionalProps {
  isEdicao: boolean;
  setIsEdicao: React.Dispatch<React.SetStateAction<boolean>>;
  setBaseDeCalculo: React.Dispatch<
    // eslint-disable-next-line no-undef
    React.SetStateAction<PaginasOutrosRegimes>
  >;
}

const VendaNacional = ({ isEdicao, setIsEdicao, setBaseDeCalculo }: IVendaNacionalProps) => {
  const [isEdicaoNcm, setIsEdicaoNcm] = React.useState<boolean>(false);
  const [isStepNcm, setIsStepNcm] = React.useState(isEdicao);

  const { deleteVendaNacional, getVendaNacional } = useBackEndRequests();
  const context = useContext(DiefCfemContext);

  const mostrarNotificacao = useNotificacao();
  const [isModalOpened, setIsModalOpened] = useState(false);

  const {
    data: vendaNacionalData,
    isLoading: isVendaNacionalDataLoading,
    error: vendaNacionalDataError,
    refetch: refetchVendaNacionalData,
  } = useQuery<IVendaNacional, IErrorWithSentryId>({
    queryKey: ['getVendaNacional', context?.idVendaNacional],
    queryFn: () => getVendaNacional(context?.idVendaNacional ?? -1),
    refetchOnWindowFocus: true,
    retryDelay: 100,
    enabled: context?.idVendaNacional !== -1,
  });

  const deleteVenda = async () => {
    await deleteVendaNacional(context?.idVendaNacional || -1)
      .then(() => {
        setIsEdicao(false);
        setBaseDeCalculo('lista');
        setIsModalOpened(false);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao cancelar Venda em Território Nacional, tente novamente.',
          ...(err && { complementoSentryId: err }),
        });
      });
  };

  useEffect(() => {}, [vendaNacionalData]);

  return (
    <div className={styles.container}>
      {vendaNacionalDataError ? (
        <ErrorMessage
          errorMessage={''}
          error={vendaNacionalDataError}
          onClickFunction={() => {
            setIsEdicao(false);
            setBaseDeCalculo('lista');
          }}
        />
      ) : (
        <>
          {isVendaNacionalDataLoading ? (
            <div className={styles.loadingContainer}>
              <BrLoading large />
            </div>
          ) : (
            <>
              <Modal title='Excluir Venda Nacional selecionada' useScrim modalOpened={isModalOpened}>
                <Modal.Body>Deseja mesmo deletar a Venda Nacional selecionada?</Modal.Body>
                <Modal.Footer justify-content='end'>
                  <Button secondary small m={2} onClick={() => setIsModalOpened(false)}>
                    Cancelar
                  </Button>
                  <CustomConfirmer asyncCall={() => deleteVenda()}>Confirmar</CustomConfirmer>
                </Modal.Footer>
              </Modal>
              <div className={styles.titleContainer}>
                <span className={styles.title}>
                  Operação - Venda no Território Nacional Entre Empresas Coligadas ou do Mesmo Grupo Econômico
                </span>
              </div>
              <div className={isStepNcm ? styles.componentesBaseCalculo : styles.componentesBaseCalculoHidden}>
                {isStepNcm && (
                  <div className={styles.insideTitle}>
                    <span>Componentes da Base de Cálculo da CFEM</span>
                  </div>
                )}
                <div>
                  <DadosPessoaRelacionadaForm
                    isReadOnly={isStepNcm}
                    setIsStepNcm={setIsStepNcm}
                    isEdicao={isEdicao}
                    setIsEdicao={setIsEdicao}
                    vendaPessoaRelacionadaData={vendaNacionalData}
                    isLoading={isVendaNacionalDataLoading}
                    refetchDadosAnteriores={refetchVendaNacionalData}
                  />
                </div>
                {isStepNcm && (
                  <div className={styles.ncmContainer}>
                    <ComponenteNCM
                      isEdicaoNcm={isEdicaoNcm}
                      setIsEdicaoNcm={setIsEdicaoNcm}
                      ncmListaData={vendaNacionalData?.listaNcm || []}
                      isListaNCMLoading={isVendaNacionalDataLoading}
                      listaNCMError={vendaNacionalDataError}
                      type={ETipoOperacao.VENDA_TERRITORIO_NACIONAL_EMPRESAS_COLIGADAS_OU_MESMO_GRUPO_ECONOMICO}
                      refetchDadosAnteriores={refetchVendaNacionalData}
                      refetchListaNCM={refetchVendaNacionalData}
                    />
                  </div>
                )}
              </div>

              <div className={styles.containerButton}>
                {isEdicao || isStepNcm ? (
                  <BrButton secondary onClick={() => setIsModalOpened(true)}>
                    <span className={styles.button}>
                      <i className={`fas fa-trash`} /> Cancelar
                    </span>
                  </BrButton>
                ) : (
                  <BrButton secondary onClick={() => setBaseDeCalculo('lista')}>
                    <span className={styles.button}>
                      <i className={`fas fa-arrow-left`} /> Voltar
                    </span>
                  </BrButton>
                )}
                <BrButton
                  onClick={() =>
                    vendaNacionalData?.listaNcm?.length === 0
                      ? mostrarNotificacao({
                          tipo: 'danger',
                          conteudo: 'Insira pelo menos um NCM para continuar.',
                        })
                      : setBaseDeCalculo('lista')
                  }
                  primary
                  disabled={!isStepNcm}
                >
                  <span className={styles.button}>
                    {isEdicao ? (
                      <>
                        <i className={`fas fa-save`} /> Salvar Base de Cálculo
                      </>
                    ) : (
                      <>
                        <i className={`fas fa-plus`} /> Adicionar Base de Cálculo
                      </>
                    )}
                  </span>
                </BrButton>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default VendaNacional;
